import React, { useState, useRef } from 'react';
import { X, Image, Loader2 } from 'lucide-react';
import html2canvas from 'html2canvas';

interface QuoteSocialImageModalProps {
  quote: string;
  sermonTitle?: string;
  onClose: () => void;
}

type ThemeId = 'midnight' | 'gold' | 'crimson' | 'ocean' | 'forest' | 'dusk';
type FontStyleId = 'classic' | 'bold' | 'minimal';
type FormatId = 'square' | 'portrait';

const FORMATS = [
  { id: 'square' as FormatId,   label: 'Square',   ratio: '1:1', width: 1080, height: 1080 },
  { id: 'portrait' as FormatId, label: 'Portrait',  ratio: '4:5', width: 1080, height: 1350 },
];

// Preview display width in px. Scale = PREVIEW_W / 1080.
const PREVIEW_W = 270;
const PREVIEW_SCALE = PREVIEW_W / 1080; // 0.25

const SOCIAL_THEMES = [
  {
    id: 'midnight' as ThemeId,
    label: 'Midnight',
    gradient: 'linear-gradient(135deg, #1C2B39 0%, #0d1721 50%, #1C2B39 100%)',
    quoteColor: '#F2E8D5',
    attributionColor: 'rgba(242,232,213,0.55)',
    decorColor: '#2874A6',
    swatchFrom: '#1C2B39',
    swatchTo: '#2874A6',
  },
  {
    id: 'gold' as ThemeId,
    label: 'Gold',
    gradient: 'linear-gradient(135deg, #2C1F05 0%, #4a3410 50%, #1C2B39 100%)',
    quoteColor: '#F2E8D5',
    attributionColor: 'rgba(217,164,65,0.7)',
    decorColor: '#D9A441',
    swatchFrom: '#D9A441',
    swatchTo: '#2C1F05',
  },
  {
    id: 'crimson' as ThemeId,
    label: 'Crimson',
    gradient: 'linear-gradient(135deg, #2D0A0B 0%, #5a1215 50%, #1C2B39 100%)',
    quoteColor: '#F2E8D5',
    attributionColor: 'rgba(178,42,47,0.7)',
    decorColor: '#B22A2F',
    swatchFrom: '#B22A2F',
    swatchTo: '#2D0A0B',
  },
  {
    id: 'ocean' as ThemeId,
    label: 'Ocean',
    gradient: 'linear-gradient(135deg, #0a1628 0%, #0e2d4a 50%, #1a4a6b 100%)',
    quoteColor: '#e8f4fc',
    attributionColor: 'rgba(40,116,166,0.8)',
    decorColor: '#2874A6',
    swatchFrom: '#2874A6',
    swatchTo: '#0a1628',
  },
  {
    id: 'forest' as ThemeId,
    label: 'Forest',
    gradient: 'linear-gradient(135deg, #0d1f16 0%, #1a3d2b 50%, #0d2b1f 100%)',
    quoteColor: '#e8f5ee',
    attributionColor: 'rgba(45,127,94,0.75)',
    decorColor: '#2D7F5E',
    swatchFrom: '#2D7F5E',
    swatchTo: '#0d1f16',
  },
  {
    id: 'dusk' as ThemeId,
    label: 'Dusk',
    gradient: 'linear-gradient(135deg, #1a0d2e 0%, #2e1a3d 35%, #4a1a2a 70%, #6b2c1a 100%)',
    quoteColor: '#f5e8f0',
    attributionColor: 'rgba(232,106,51,0.7)',
    decorColor: '#E86A33',
    swatchFrom: '#E86A33',
    swatchTo: '#1a0d2e',
  },
];

const FONT_STYLES = [
  {
    id: 'classic' as FontStyleId,
    label: 'Classic',
    quoteFontFamily: '"DM Serif Display", Georgia, serif',
    quoteFontWeight: '400',
    quoteFontStyle: 'italic',
    quoteFontSizeBase: 72,
    lineHeight: '1.25',
    letterSpacing: '0.01em',
  },
  {
    id: 'bold' as FontStyleId,
    label: 'Bold',
    quoteFontFamily: '"DM Serif Display", Georgia, serif',
    quoteFontWeight: '400',
    quoteFontStyle: 'normal',
    quoteFontSizeBase: 80,
    lineHeight: '1.15',
    letterSpacing: '-0.01em',
  },
  {
    id: 'minimal' as FontStyleId,
    label: 'Minimal',
    quoteFontFamily: 'Inter, sans-serif',
    quoteFontWeight: '300',
    quoteFontStyle: 'normal',
    quoteFontSizeBase: 64,
    lineHeight: '1.4',
    letterSpacing: '0.03em',
  },
];

function computeFontSize(base: number, quoteLength: number): string {
  if (quoteLength > 220) return `${Math.floor(base * 0.7)}px`;
  if (quoteLength > 130) return `${Math.floor(base * 0.85)}px`;
  return `${base}px`;
}

export function QuoteSocialImageModal({ quote, sermonTitle, onClose }: QuoteSocialImageModalProps) {
  const [selectedThemeId, setSelectedThemeId] = useState<ThemeId>('midnight');
  const [selectedFontId, setSelectedFontId] = useState<FontStyleId>('classic');
  const [selectedFormatId, setSelectedFormatId] = useState<FormatId>('square');
  const [isCapturing, setIsCapturing] = useState(false);
  const captureRef = useRef<HTMLDivElement>(null);

  const theme = SOCIAL_THEMES.find(t => t.id === selectedThemeId)!;
  const fontStyle = FONT_STYLES.find(f => f.id === selectedFontId)!;
  const format = FORMATS.find(f => f.id === selectedFormatId)!;

  const previewH = Math.round(PREVIEW_W * (format.height / format.width));

  const handleDownload = async () => {
    if (!captureRef.current) return;
    setIsCapturing(true);
    try {
      await document.fonts.ready;
      await new Promise(r => setTimeout(r, 300));
      const canvas = await html2canvas(captureRef.current, {
        width: format.width,
        height: format.height,
        scale: 1,
        useCORS: true,
        allowTaint: false,
        logging: false,
        onclone: (clonedDoc) => {
          const style = clonedDoc.createElement('style');
          style.textContent = `@import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=Inter:wght@300;400;500;600;700&display=swap');`;
          clonedDoc.head.appendChild(style);
        },
      });
      const dataUrl = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = `sermon-quote-${selectedFormatId}.png`;
      link.href = dataUrl;
      link.click();
    } finally {
      setIsCapturing(false);
    }
  };

  // Canvas layers — rendered twice: once in the off-screen capture div, once in the preview.
  const canvasLayers = (
    <>
      {/* Layer 1: subtle radial depth overlay */}
      <div style={{
        position: 'absolute', inset: 0,
        background: 'radial-gradient(ellipse at 20% 80%, rgba(255,255,255,0.06), transparent 60%)',
      }} />

      {/* Layer 2: accent bar + giant open-quote glyph */}
      <div style={{ position: 'absolute', top: 108, left: 72 }}>
        <div style={{ width: 4, height: 80, background: theme.decorColor, borderRadius: 2 }} />
        <span style={{
          position: 'absolute', top: -20, left: 20,
          fontFamily: '"DM Serif Display", Georgia, serif',
          fontSize: 280, lineHeight: 1,
          color: theme.decorColor, opacity: 0.12,
          fontStyle: 'italic', userSelect: 'none',
        }}>"</span>
      </div>

      {/* Layer 3: quote text */}
      <div style={{
        position: 'absolute', inset: 0,
        padding: `${format.height === 1350 ? 220 : 200}px 88px`,
        display: 'flex', alignItems: 'center',
      }}>
        <p style={{
          fontFamily: fontStyle.quoteFontFamily,
          fontWeight: fontStyle.quoteFontWeight,
          fontStyle: fontStyle.quoteFontStyle,
          fontSize: computeFontSize(fontStyle.quoteFontSizeBase, quote.length),
          lineHeight: fontStyle.lineHeight,
          letterSpacing: fontStyle.letterSpacing,
          color: theme.quoteColor,
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
          margin: 0,
        }}>
          {quote}
        </p>
      </div>

      {/* Layer 4: attribution footer */}
      <div style={{ position: 'absolute', bottom: 88, left: 88, right: 88 }}>
        <div style={{ height: 1, background: theme.decorColor, opacity: 0.2, marginBottom: 24 }} />
        {sermonTitle && (
          <p style={{
            fontFamily: 'Inter, sans-serif', fontSize: 28, fontWeight: 500,
            color: theme.attributionColor, margin: '0 0 8px 0',
          }}>{sermonTitle}</p>
        )}
        <p style={{
          fontFamily: 'Inter, sans-serif', fontSize: 20, fontWeight: 400,
          color: theme.attributionColor, opacity: 0.45,
          letterSpacing: '0.15em', margin: 0,
        }}>sermonbuild.com</p>
      </div>
    </>
  );

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-sand/20">
          <h2 className="text-lg font-semibold text-midnight">Create Social Image</h2>
          <button onClick={onClose} className="p-1.5 rounded-md text-slate hover:bg-sand/20 transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/*
          Off-screen capture target — full native size, no transform.
          html2canvas reads this div directly; the CSS transform approach
          was causing content to render at scaled size inside the canvas.
        */}
        <div
          ref={captureRef}
          aria-hidden="true"
          style={{
            position: 'fixed',
            left: '-99999px',
            top: 0,
            width: format.width,
            height: format.height,
            background: theme.gradient,
            overflow: 'hidden',
          }}
        >
          {canvasLayers}
        </div>

        <div className="p-6 flex flex-col sm:flex-row gap-6">
          {/* Live preview (CSS-scaled clone, purely visual) */}
          <div className="flex-shrink-0 flex flex-col items-center gap-2">
            <div style={{
              width: PREVIEW_W,
              height: previewH,
              overflow: 'hidden',
              borderRadius: 8,
              flexShrink: 0,
              position: 'relative',
            }}>
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: format.width,
                height: format.height,
                transform: `scale(${PREVIEW_SCALE})`,
                transformOrigin: 'top left',
                background: theme.gradient,
              }}>
                {canvasLayers}
              </div>
            </div>
            <p className="text-xs text-slate/50">{format.width} × {format.height} px</p>
          </div>

          {/* Controls */}
          <div className="flex-1 flex flex-col gap-5">
            {/* Format */}
            <div>
              <p className="text-sm font-medium text-midnight mb-2">Format</p>
              <div className="flex gap-2">
                {FORMATS.map(f => (
                  <button
                    key={f.id}
                    onClick={() => setSelectedFormatId(f.id)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                      selectedFormatId === f.id
                        ? 'bg-electric text-white'
                        : 'border border-sand/30 text-slate hover:border-electric/40 bg-white'
                    }`}
                  >
                    {f.label} <span className="opacity-60 text-xs">{f.ratio}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Theme swatches */}
            <div>
              <p className="text-sm font-medium text-midnight mb-2">Theme</p>
              <div className="flex flex-wrap gap-2">
                {SOCIAL_THEMES.map(t => (
                  <button
                    key={t.id}
                    onClick={() => setSelectedThemeId(t.id)}
                    title={t.label}
                    className={`w-8 h-8 rounded-full transition-all ${
                      selectedThemeId === t.id
                        ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-100 scale-110'
                        : 'hover:scale-105'
                    }`}
                    style={{ background: `linear-gradient(135deg, ${t.swatchFrom}, ${t.swatchTo})` }}
                  />
                ))}
              </div>
              <p className="text-xs text-slate/60 mt-1.5">{theme.label}</p>
            </div>

            {/* Font style pills */}
            <div>
              <p className="text-sm font-medium text-midnight mb-2">Font Style</p>
              <div className="flex gap-2">
                {FONT_STYLES.map(f => (
                  <button
                    key={f.id}
                    onClick={() => setSelectedFontId(f.id)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                      selectedFontId === f.id
                        ? 'bg-electric text-white'
                        : 'border border-sand/30 text-slate hover:border-electric/40 bg-white'
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Quote text */}
            <div className="flex-1 min-h-0">
              <p className="text-sm font-medium text-midnight mb-1.5">Quote</p>
              <p className="text-sm text-slate italic leading-relaxed line-clamp-4">"{quote}"</p>
            </div>

            {/* Download */}
            <button
              onClick={handleDownload}
              disabled={isCapturing}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-electric text-white rounded-lg hover:bg-electric/90 disabled:opacity-60 disabled:cursor-not-allowed font-medium transition-colors"
            >
              {isCapturing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Generating…
                </>
              ) : (
                <>
                  <Image className="h-4 w-4" />
                  Download PNG
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
